import {
  FeatureFlag,
  type ParticipantDocument,
  Role,
  ScoringResult,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  NumberedBadge,
  ScrollArea,
  Select,
  type SelectOption,
  typedMemo,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { FaBell, FaMagic } from 'react-icons/fa';
import { UserMultiChooser } from '../../components/forms/UserMultiChooser';
import { showModal } from '../../components/generic/Modal';
import type {
  CommentStatus,
  CommentThread,
} from '../../components/tiptap/extensions/comment/Comment';
import { CommentThreadContentMemo } from '../../components/tiptap/menus/comment/CommentThreadContent';
import { useNotifyParticipantDocumentAuthorsMutation } from '../../graphql/hooks/useNotifyParticipantDocumentAuthorsMutation';
import { useNotifyParticipantDocumentReviewersMutation } from '../../graphql/hooks/useNotifyParticipantDocumentReviewersMutation';
import { useUpdateParticipantDocumentMutation } from '../../graphql/hooks/useUpdateParticipantDocumentMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { useRole } from '../../hooks/useRole';
import { ParticipantDocumentScoringResultModal } from './ParticipantDocumentScoringResultModal';
import { ParticipantDocumentStatusMenu } from './ParticipantDocumentStatusMenu';

interface StatusProps {
  participantDocument: ParticipantDocument;
}

export const Status = (props: StatusProps) => {
  const { participantDocument } = props;

  return (
    <ChakraV3.Stack py={2} px={3} align='start'>
      <ChakraV3.Text fontWeight='semibold' fontSize='sm'>
        Status
      </ChakraV3.Text>
      <ChakraV3.Stack>
        <ParticipantDocumentStatusMenu participantDocument={participantDocument} />
      </ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};

interface AiReviewProps {
  participantDocument: ParticipantDocument;
}

export const AiReview = (props: AiReviewProps) => {
  const { participantDocument } = props;
  const { organization } = useAppContext();

  const isSupportRole = useRole(Role.Support);
  const hasPermission = organization.hasFeature(FeatureFlag.TemplateScoring) || isSupportRole;

  const scoringResult = participantDocument.scoringResult ?? null;

  const openModal = () =>
    showModal(ParticipantDocumentScoringResultModal, {
      participantId: participantDocument.participantId,
      participantDocumentId: participantDocument.id,
      scoringResult,
    });

  if (!participantDocument.template?.scoringChecklist || !hasPermission) {
    return null;
  }

  const summary = P.run(() => {
    if (!scoringResult) {
      return null;
    }

    const passedCount = scoringResult.results.filter(
      (r) => r.result === ScoringResult.Passed,
    ).length;
    const failedCount = scoringResult.results.filter(
      (r) => r.result === ScoringResult.Failed,
    ).length;
    const totalCount = scoringResult.results.length;

    const criteria = scoringResult.checklist.categories.flatMap((c) => c.criterias);
    const labelCounts = scoringResult.checklist.criteriaLabels
      .map((label) => {
        const labelCriteria = criteria.filter((c) => c.labelIds.includes(label.id));
        const labelResults = scoringResult.results.filter((r) =>
          labelCriteria.some((c) => c.id === r.id),
        );
        return {
          name: label.name,
          color: label.color,
          total: labelCriteria.length,
          passing: labelResults.filter((r) => r.result === ScoringResult.Passed).length,
        };
      })
      .filter((l) => l.total > 0);

    return (
      <ChakraV3.Stack gap={2} w='full'>
        <ChakraV3.HStack gap={2} w='full' lineHeight={1.1}>
          <ChakraV3.Stack
            p={2}
            rounded='md'
            color='green.500'
            bg='green.50'
            align='center'
            w='full'
            gap={0}
            justify='center'
          >
            <ChakraV3.Text fontSize='lg' fontWeight='bold'>
              {passedCount}
            </ChakraV3.Text>
            <ChakraV3.Text fontSize='xs'>Passed</ChakraV3.Text>
          </ChakraV3.Stack>
          <ChakraV3.Stack
            p={2}
            rounded='md'
            color='red.500'
            bg='red.50'
            align='center'
            w='full'
            gap={0}
            justify='center'
          >
            <ChakraV3.Text fontSize='lg' fontWeight='bold'>
              {failedCount}
            </ChakraV3.Text>
            <ChakraV3.Text fontSize='xs'>Failed</ChakraV3.Text>
          </ChakraV3.Stack>
          <ChakraV3.Stack
            p={2}
            rounded='md'
            color='gray.500'
            bg='gray.50'
            align='center'
            w='full'
            gap={0}
            justify='center'
          >
            <ChakraV3.Text fontSize='lg' fontWeight='bold'>
              {totalCount}
            </ChakraV3.Text>
            <ChakraV3.Text fontSize='xs'>Total</ChakraV3.Text>
          </ChakraV3.Stack>
        </ChakraV3.HStack>
        {labelCounts.map((l) => (
          <ChakraV3.HStack key={l.name} justify='space-between' align='center'>
            <ChakraV3.Tag size='sm' colorPalette={l.color}>
              <ChakraV3.TagLabel>{l.name}</ChakraV3.TagLabel>
            </ChakraV3.Tag>
            <ChakraV3.Text fontSize='sm' color='secondary'>
              {l.passing}/{l.total}
            </ChakraV3.Text>
          </ChakraV3.HStack>
        ))}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Stack py={2} px={3}>
      <ChakraV3.Text fontWeight='semibold' fontSize='sm'>
        AI Review
      </ChakraV3.Text>
      {summary}
      <ChakraV3.Button onClick={openModal} size='2xs' variant='subtle' mt={scoringResult ? 3 : 0}>
        <ChakraV3.Icon boxSize='10px'>
          <FaMagic />
        </ChakraV3.Icon>
        {scoringResult ? 'View AI review' : 'Review with AI'}
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};

interface AuthorsProps {
  participantDocument: ParticipantDocument;
}

export const Authors = (props: AuthorsProps) => {
  const { participantDocument } = props;

  const { organization, user, successToast, errorToast } = useAppContext();
  const updateParticipantDocumentMutation = useUpdateParticipantDocumentMutation();
  const notifyAuthorsMutation = useNotifyParticipantDocumentAuthorsMutation();

  const authorIds = participantDocument.authors.map((author) => author.id) ?? [];

  const onClick = async () => {
    await notifyAuthorsMutation
      .mutateAsync({
        organizationId: organization.id,
        participantId: participantDocument.participantId,
        participantDocumentId: participantDocument.id,
        userId: user.id,
      })
      .then(async () => {
        successToast('Participant document authors notified successfully');
      })
      .catch((err) => {
        errorToast(`Error notifying participant document authors: ${err.message}`);
      });
  };

  const onChange = async (authors: string[]) => {
    await updateParticipantDocumentMutation
      .mutateAsync({
        organizationId: organization.id,
        participantId: participantDocument.participantId,
        participantDocumentId: participantDocument.id,
        request: {
          authorIds: authors,
        },
      })
      .catch((err) => {
        errorToast(`Error updating participant document authors: ${err.message}`);
      });
  };

  return (
    <ChakraV3.Stack py={2} px={3}>
      <ChakraV3.Text fontWeight='semibold' fontSize='sm'>
        Authors
      </ChakraV3.Text>

      <UserMultiChooser
        onChange={onChange}
        value={authorIds}
        participantId={participantDocument.participantId}
        placeholder='Select some authors'
      />
      <ChakraV3.Button
        onClick={onClick}
        size='2xs'
        variant='subtle'
        loading={notifyAuthorsMutation.isLoading}
        loadingText='Notifying authors...'
      >
        <ChakraV3.Icon boxSize='10px'>
          <FaBell />
        </ChakraV3.Icon>
        Notify authors
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};

interface ReviewersProps {
  participantDocument: ParticipantDocument;
}

export const Reviewers = (props: ReviewersProps) => {
  const { participantDocument } = props;

  const { organization, user, successToast, errorToast } = useAppContext();
  const updateParticipantDocumentMutation = useUpdateParticipantDocumentMutation();
  const notifyReviewersMutation = useNotifyParticipantDocumentReviewersMutation();

  const reviewerIds = participantDocument.reviewers.map((reviewer) => reviewer.id) ?? [];

  const onClick = async () => {
    await notifyReviewersMutation
      .mutateAsync({
        organizationId: organization.id,
        participantId: participantDocument.participantId,
        participantDocumentId: participantDocument.id,
        userId: user.id,
      })
      .then(async () => {
        successToast('Participant document reviewers notified successfully');
      })
      .catch((err) => {
        errorToast(`Error notifying participant document reviewers: ${err.message}`);
      });
  };

  const onChange = async (reviewers: string[]) => {
    await updateParticipantDocumentMutation
      .mutateAsync({
        organizationId: organization.id,
        participantId: participantDocument.participantId,
        participantDocumentId: participantDocument.id,
        request: {
          reviewerIds: reviewers,
        },
      })
      .catch((err) => {
        errorToast(`Error updating participant document reviewers: ${err.message}`);
      });
  };

  return (
    <ChakraV3.Stack py={2} px={3}>
      <ChakraV3.Text fontWeight='semibold' fontSize='sm'>
        Reviewers
      </ChakraV3.Text>

      <UserMultiChooser
        onChange={onChange}
        value={reviewerIds}
        participantId={participantDocument.participantId}
        placeholder='Select some reviewers'
      />
      <ChakraV3.Button
        onClick={onClick}
        size='2xs'
        variant='subtle'
        loading={notifyReviewersMutation.isLoading}
        loadingText='Notifying reviewers...'
      >
        <ChakraV3.Icon boxSize='10px'>
          <FaBell />
        </ChakraV3.Icon>
        Notify reviewers
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};

interface CommentsProps {
  commentThreads: CommentThread[];
  editor: Editor;
}

const filterOptions: SelectOption<CommentStatus>[] = [
  { label: 'Open', value: 'open', raw: 'open' },
  { label: 'Resolved', value: 'resolved', raw: 'resolved' },
];

export const Comments = (props: CommentsProps) => {
  const { commentThreads, editor } = props;

  const [filter, setFilter] = React.useState<SelectOption<CommentStatus>>({
    label: 'Open',
    value: 'open',
    raw: 'open',
  });

  const latestActivity = (thread: CommentThread) =>
    P.maxBy(thread.comments, (comment) => comment.time)?.time ?? 0;

  const filteredCommentThreads = P.orderBy(
    commentThreads.filter((thread) => (thread.status ?? 'open') === filter.value),
    latestActivity,
    'desc',
  );

  const content = P.run(() => {
    if (P.isEmpty(filteredCommentThreads)) {
      return (
        <ChakraV3.Result
          status='success'
          title={`You're all caught up`}
          description={
            <ChakraV3.Text fontSize='sm' color='secondary'>
              No comments to review
            </ChakraV3.Text>
          }
        />
      );
    }

    return (
      <ChakraV3.Stack gap={0} separator={<ChakraV3.Separator />} mb={32}>
        {filteredCommentThreads.map((thread) => {
          return (
            <CommentThreadContentMemo
              key={thread.id}
              commentThread={thread}
              editor={editor}
              py={4}
              pl={2}
              pr={4}
              _hover={{
                bg: 'gray.50',
              }}
            />
          );
        })}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Stack gap={0}>
      <ChakraV3.HStack py={2} px={3}>
        <ChakraV3.Text fontWeight='semibold' fontSize='sm'>
          Comments
        </ChakraV3.Text>
        <ChakraV3.Spacer />
        <Select
          options={filterOptions}
          value={filter}
          onChange={setFilter}
          variant='ghost'
          w='fit-content'
          size='xs'
          showArrow={false}
          components={{
            Header: () => null,
            Value: (props) => (
              <ChakraV3.HStack>
                {props.option.label}
                <NumberedBadge fontSize='9' count={filteredCommentThreads.length} />
              </ChakraV3.HStack>
            ),
          }}
        />
      </ChakraV3.HStack>
      {content}
    </ChakraV3.Stack>
  );
};

interface ParticipantDocumentReviewSectionProps {
  participantDocument: ParticipantDocument;
  commentThreads: CommentThread[];
  editor: Editor;
}

export const ParticipantDocumentReviewSection = (props: ParticipantDocumentReviewSectionProps) => {
  const { commentThreads, participantDocument, editor } = props;

  return (
    <ScrollArea w='full' h='full'>
      <ChakraV3.Stack>
        <Status participantDocument={participantDocument} />
        <ChakraV3.Separator />
        <ChakraV3.Stack gap={0}>
          <AiReview participantDocument={participantDocument} />
          <Authors participantDocument={participantDocument} />
          <Reviewers participantDocument={participantDocument} />
        </ChakraV3.Stack>
        <ChakraV3.Separator />
        <Comments commentThreads={commentThreads} editor={editor} />
      </ChakraV3.Stack>
    </ScrollArea>
  );
};

export const ParticipantDocumentReviewSectionMemo = typedMemo(ParticipantDocumentReviewSection);
